import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { NEVER_QUEUED } from '@efacture/web-billing/lib/billing-mutations';
import type { SfeIsf } from '@/api/queries/settings';

// ─────────────────────────────────────────────────────────────
// Types (miroir du modèle Prisma — Tâche 7 API + Tâche 10 web)
// ─────────────────────────────────────────────────────────────

export interface Establishment {
  id: string;
  code: string;
  name: string;
  address: string | null;
  /** §3 c: section, ilot and parcelle, SSSS LLL PPPP. */
  cadastralReference: string | null;
  nimPrefix: string | null;
  isf: string | null;
  numberPrefix: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EstablishmentInput {
  code: string;
  name: string;
  address?: string | null;
  cadastralReference?: string | null;
  nimPrefix?: string | null;
  isf?: string | null;
  numberPrefix?: string | null;
}

export interface EstablishmentUpdateInput {
  name?: string;
  address?: string | null;
  cadastralReference?: string | null;
  nimPrefix?: string | null;
  isf?: string | null;
  numberPrefix?: string | null;
  isActive?: boolean;
}

/** A cancelled number of a series, as the numbering register lists it. */
export interface CancelledSeriesNumber {
  number: string;
  rank: number;
  /** « Numéro présenté au MCF, refusé, annulé » (from the server). */
  label: string;
  reason: string;
  cancelledAt: string;
  cancelledBy: string | null;
}

/**
 * A number retired by the arbitration of a signature in doubt declared not
 * signed, or by the regularisation of a number the MCF certified that no
 * invoice carries; `label` says which.
 */
export interface RetiredSeriesNumber {
  number: string;
  rank: number;
  /** French register label, from the server. */
  label: string;
  reason: string;
  retiredAt: string;
  retiredBy: string | null;
}

/** One numbering series (site × invoice type × fiscal year), GET /establishments/:id/numbering. */
export interface NumberingSeries {
  invoiceType: string;
  fiscalYear: number;
  lastNumber: number;
  nextNumber: string;
  /** True while the last issued number can be entered. */
  empty: boolean;
  /** Why the series is not empty (French, from the server). */
  usage: string | null;
  /** Numbers presented to the MCF, refused and cancelled by an administrator. */
  cancelledNumbers: CancelledSeriesNumber[];
  /** Numbers retired by an arbitration declaring that the MCF did not sign. */
  retiredNumbers: RetiredSeriesNumber[];
  lastIssued: {
    value: number;
    number: string;
    reason: string;
    recordedAt: string;
    recordedBy: string | null;
  } | null;
}

/** The answer to an entry: `warning` (French) when the fiscal counter witness was not written. */
export interface RecordedLastIssued extends NumberingSeries {
  warning: string | null;
}

export interface EstablishmentNumbering {
  establishmentId: string;
  establishmentCode: string;
  establishmentName: string;
  fiscalYear: number;
  series: NumberingSeries[];
  /** French notice, present only when another install issues the series. */
  issuingNotice?: string;
}

export interface LastIssuedInput {
  invoiceType: string;
  fiscalYear: number;
  lastIssuedNumber: number;
  reason: string;
}

// ─────────────────────────────────────────────────────────────
// Réponses (tableaux nommés contextuellement — pitfall CLAUDE.md)
// ─────────────────────────────────────────────────────────────

export interface EstablishmentListResponse {
  establishments: Establishment[];
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const establishmentKeys = {
  all: ['establishments'] as const,
  lists: () => [...establishmentKeys.all, 'list'] as const,
  list: (includeInactive: boolean) => [...establishmentKeys.lists(), { includeInactive }] as const,
  details: () => [...establishmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...establishmentKeys.details(), id] as const,
  numbering: (id: string) => [...establishmentKeys.detail(id), 'numbering'] as const,
  numberingYear: (id: string, fiscalYear: number | null) =>
    [...establishmentKeys.numbering(id), fiscalYear] as const,
  defaultNumbering: () => [...establishmentKeys.all, 'default', 'numbering'] as const,
  sfeIsf: () => [...establishmentKeys.all, 'sfe-isf'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchEstablishments(includeInactive: boolean): Promise<EstablishmentListResponse> {
  return apiClient.get<EstablishmentListResponse>('/establishments', {
    includeInactive: includeInactive || undefined,
  });
}

async function fetchSfeIsf(): Promise<SfeIsf> {
  const result = await apiClient.get<{ sfeIsf: SfeIsf }>('/establishments/sfe-isf');
  return result.sfeIsf;
}

async function createEstablishment(data: EstablishmentInput): Promise<Establishment> {
  const result = await apiClient.post<{ establishment: Establishment }>('/establishments', data);
  return result.establishment;
}

async function updateEstablishment(
  id: string,
  data: EstablishmentUpdateInput
): Promise<Establishment> {
  const result = await apiClient.patch<{ establishment: Establishment }>(
    `/establishments/${id}`,
    data
  );
  return result.establishment;
}

async function fetchEstablishmentNumbering(
  id: string,
  fiscalYear: number | null
): Promise<EstablishmentNumbering> {
  const result = await apiClient.get<{ numbering: EstablishmentNumbering }>(
    `/establishments/${id}/numbering`,
    fiscalYear === null ? undefined : { fiscalYear }
  );
  return result.numbering;
}

async function fetchDefaultEstablishmentNumbering(): Promise<EstablishmentNumbering> {
  const result = await apiClient.get<{ numbering: EstablishmentNumbering }>(
    '/establishments/default/numbering',
    undefined
  );
  return result.numbering;
}

// A queued replay could land after the series has started and be refused:
// the entry fails now instead.
async function recordLastIssued(id: string, data: LastIssuedInput): Promise<RecordedLastIssued> {
  const result = await apiClient.post<{ series: RecordedLastIssued }>(
    `/establishments/${id}/numbering/last-issued`,
    data,
    { noQueue: true }
  );
  return result.series;
}

// ─────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────

/**
 * Liste des établissements du tenant. GET /api/establishments
 * `includeInactive` = false par défaut (actifs seulement, cf. route API).
 */
export function useEstablishments(
  includeInactive = false,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: establishmentKeys.list(includeInactive),
    queryFn: () => fetchEstablishments(includeInactive),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * The running SFE build's attestation ISF. GET /api/establishments/sfe-isf
 * Once it is attested, the establishment form shows it read-only.
 */
export function useSfeIsf(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: establishmentKeys.sfeIsf(),
    queryFn: fetchSfeIsf,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/** Création d'un établissement. POST /api/establishments (establishments:write) */
export function useCreateEstablishment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EstablishmentInput) => createEstablishment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: establishmentKeys.lists() });
    },
  });
}

/**
 * Modification d'un établissement (nom/adresse/nimPrefix/isf/numberPrefix
 * et/ou désactivation-réactivation via `isActive`). PATCH /api/establishments/:id
 * (establishments:write)
 */
export function useUpdateEstablishment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EstablishmentUpdateInput }) =>
      updateEstablishment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: establishmentKeys.lists() });
      // Un utilisateur peut être affecté à un établissement désactivé/réactivé
      // — rafraîchir la liste des utilisateurs pour refléter tout blocage.
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

/**
 * Series of a site for `fiscalYear`, the current one when null.
 * GET /api/establishments/:id/numbering[?fiscalYear=] (tenant:settings)
 */
export function useEstablishmentNumbering(id: string, fiscalYear: number | null = null) {
  return useQuery({
    queryKey: establishmentKeys.numberingYear(id, fiscalYear),
    queryFn: () => fetchEstablishmentNumbering(id, fiscalYear),
  });
}

/**
 * Series of the tenant's main establishment, with its id, code and name.
 * GET /api/establishments/default/numbering (tenant:settings)
 */
export function useDefaultEstablishmentNumbering() {
  return useQuery({
    queryKey: establishmentKeys.defaultNumbering(),
    queryFn: fetchDefaultEstablishmentNumbering,
  });
}

/** Last issued number of an empty series. POST /api/establishments/:id/numbering/last-issued (tenant:settings) */
export function useRecordLastIssued(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (data: LastIssuedInput) => recordLastIssued(id, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: establishmentKeys.numbering(id) });
      queryClient.invalidateQueries({ queryKey: establishmentKeys.defaultNumbering() });
    },
  });
}

export {
  fetchEstablishments,
  createEstablishment,
  updateEstablishment,
  fetchEstablishmentNumbering,
  fetchDefaultEstablishmentNumbering,
  recordLastIssued,
};
